<template>
    <component
        :is="tag"
        :disabled
        :form
        :class="cn(
            {
                'transition-colors relative overflow-hidden flex justify-center items-center text-center gap-2.5 md:gap-4 border-1.5 rounded-xl md:rounded-2xl': !noStyle
            }, 
            styleClass,
            props.class)">
        <slot>
            {{ label }}
        </slot>
        <FadeInOut>
            <span v-if="isBusy" class="absolute inset-0 z-10 flex items-center justify-center bg-inherit size-full">
                <Spinner></Spinner>
            </span>
        </FadeInOut>
    </component>
</template>

<script setup lang="ts">
import { type HTMLAttributes } from 'vue'
import { FadeInOut } from '../transition'
import { Spinner } from '../spinner'
import { cn } from '@/lib/utils'
import { useEventListener } from '@vueuse/core'

interface Props {
    tag?: 'a'|'button'
    class?: HTMLAttributes['class']
    noStyle?: boolean
    buttonStyle?: 'primary'|'primary-outline'|'secondary'|'secondary-outline'|'primary-no-outline'
    buttonSize?: 'xs'|'sm'|'sm/md'|'md'|'lg'|'Nav CTA'
    label?: string
    form?: string
}

defineOptions({
    name: 'umb-submit-button'
})

const props = withDefaults(defineProps<Props>(), {
    tag: 'button',
    buttonStyle: 'secondary',
    buttonSize: 'lg'
})

const isBusy = defineModel('isBusy', { type: Boolean })
const disabled = defineModel('disabled', { type: Boolean })

let styleClass = ''

if (!props.noStyle) {
    switch(props.buttonStyle) {
        case 'primary':
            styleClass += 'bg-primary dark:bg-white border-primary dark:border-white text-white dark:text-red '
            break
        case 'primary-outline':
            styleClass += 'bg-white border-primary text-primary '
            break
        case 'secondary':
            styleClass += 'bg-secondary dark:bg-red-dark border-secondary dark:bg-red-dark text-white '
            break
        case 'secondary-outline':
            styleClass += 'bg-white dark:bg-transparent border-secondary text-secondary '
            break
        case 'primary-no-outline':
            styleClass += 'bg-white border-white text-primary '
            break
    }
    
    switch(props.buttonSize) {
        case 'xs':
            styleClass += 'py-2 px-3 body-2 font-medium'
            break
        case 'sm':
            styleClass += 'py-2.5 px-4 body-2 font-medium'
            break
        case 'sm/md':
            styleClass += 'py-2 md:py-2.5 px-3 md:px-4 body-2 font-medium'
            break
        case 'md':
            styleClass += 'py-2.5 md:py-3 px-4 md:px-6 body font-medium'
            break
        case 'lg':
            styleClass += 'py-2.5 md:py-4 px-4 md:px-9 heading-6'
            break
        case 'Nav CTA':
            styleClass += 'py-2 md:py-3 px-3 md:px-6 min-h-8 md:min-h-0 md:h-auto items-center body font-medium'
            break
    }
}


useEventListener(window, 'umb-form-submit', (e: CustomEvent) => {
    if (e.detail?.formId !== props.form) return

    isBusy.value = true
    disabled.value = true
})
</script>